import {useParams} from "react-router-dom";
import {useInjection} from "inversify-react";
import ApiService from "../../../services/apiService";
import React, {useCallback, useEffect, useState} from "react";
import {
    AddressDto,
    DeliveryMethod,
    DiscountType,
    OrderApi,
    OrderDeliveryVariantCostDto,
    OrderDetailsDto,
    OrderProductApi,
    OrderProductDto,
    OrderStage,
    PayByLink
} from "../../../api";
import styles from "./orderDetails.module.scss";
import moment from "moment";
import ShopPaymentSVG from "../../../svg/shopPayment.svg";
import SelfSVG from "../../../svg/deliveryMethods/self.svg";
import InPostCarrierSVG from "../../../svg/deliveryMethods/carrier.svg";
import InPostParcelMachineSVG from "../../../svg/deliveryMethods/parcelMachine.svg";
import deliveryMethodTranslation from "../../../helpers/deliveryMethodHelper";
import {Button, Dropdown, message, Modal, Tag} from "antd";
import OrderRefundsListContainer from "../../orderRefund/orderRefundListContainer";
import OrderProductsListContainer from "../../orderProduct/orderProductListContainer";
import {OrderPaymentTag, OrderStageTag} from "../../../helpers/OrderStageHelper";
import {AsyncButton, AsyncButtonPopconfirm} from "digimuth-components/dist";
import OrderDataFill from "./orderDataFill";
import {discountTypeTranslation} from "../../../helpers/DiscountTypeHelper";
import {InfoCircleOutlined} from "@ant-design/icons";
import {InpostPointType} from "../../../components/inpost/inpost-geowidget/inpost-geowidget";

export enum PaymentState {
    Disabled = "DISABLED",
    Enabled = "ENABLED",
    TemporaryDisabled = "TEMPORARY_DISABLED"
}

export interface PaymentMethod {
    label: string,
    value: string,
    state: PaymentState,
    minValue: number,
    icon: string | null,
    maxValue: number | undefined
}

export const DeliveryMethodEnum = [
    {
        label: "Odbiór osobisty",
        value: DeliveryMethod.SelfPickup,
        iconSrc: SelfSVG
    },
    {
        label: "InPost - Paczkomat",
        value: DeliveryMethod.InPostParcelMachine,
        iconSrc: InPostParcelMachineSVG
    },
    {
        label: "InPost - Kurier",
        value: DeliveryMethod.InPostCarrier,
        iconSrc: InPostCarrierSVG
    }
];

export default function OrderDetailsContainer() {
    const {orderId} = useParams();
    const apiService = useInjection(ApiService);

    const [showParcelMachineDetailsModal, setShowParcelMachineDetailsModal] = useState<boolean>(false);
    const [overStock, setOverStock] = useState<boolean>(false);
    const [orderDetails, setOrderDetails] = useState<OrderDetailsDto | null>(null);
    const [showFillOrderModal, setShowFillOrderModal] = useState<boolean>(false);
    const [availableDeliveryMethods, setAvailableDeliveryMethods] = useState<OrderDeliveryVariantCostDto[] | null>(null);
    const [selectedDeliveryMethod, setSelectedDeliveryMethod] = useState<DeliveryMethod | null>(null);
    const [selectedParcelMachine, setSelectedParcelMachine] = useState<InpostPointType | null>(null);
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethod | null>(null);
    const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);

    const fetchPaymentMethods = useCallback(async () => {
        try {
            setPaymentMethods(paymentMethods => [...paymentMethods.filter(x => x.value !== "ShopPayment"), {
                    label: "Płatność przy odbiorze",
                    value: "ShopPayment",
                    state: selectedDeliveryMethod === DeliveryMethod.SelfPickup
                        ? PaymentState.Enabled : PaymentState.Disabled,
                    minValue: 0,
                    maxValue: undefined,
                    icon: ShopPaymentSVG
                }]
            );

            const response = await apiService.getApi(OrderApi)
                .apiOrderAvailablePaymentMethodsGet();
            response.data.payByLinks?.forEach((method: PayByLink) => {
                if (!paymentMethods.find(x => x.value === method.value)) {
                    setPaymentMethods(paymentMethods => [...paymentMethods, {
                        label: method.name!,
                        value: method.value!,
                        state: PaymentState.Enabled,
                        minValue: method.minAmount ?? 0,
                        maxValue: method.maxAmount,
                        icon: method.brandImageUrl ?? null
                    }]);
                }
            });
        } catch (e: any) {
            return e.response?.data;
        }
    }, [apiService, selectedDeliveryMethod]);

    const fetchAvailableDeliveryMethod = useCallback(async () => {
        if (!orderDetails) return;
        try {
            const response = await apiService.getApi(OrderApi)
                .apiOrderDeliveryCostGet(orderDetails?.orderGuid);
            setAvailableDeliveryMethods(response.data);
        } catch (e: any) {
            return e.response?.data;
        }
    }, [apiService, orderDetails]);

    const fetchOrderDetails = useCallback(async () => {
        if (!orderId) return;
        try {
            const response = await apiService.getApi(OrderApi)
                .apiOrderIdGet(+orderId);
            setOrderDetails(response.data);
        } catch (e: any) {
            return e.response?.data;
        }
    }, [apiService, orderId]);

    const renderOrderAddress = (title: string, address: AddressDto) => <>
        <strong>{title}</strong>
        {address ? <div>
            <span>{address.street} {address.buildingNumber}</span>{address.flatNumber &&
            <span>/{address.flatNumber}</span>}<br/>
            <span>{address.postalCode} {address.city}</span><br/>
            {address.companyName && <span>{address.companyName}</span>}<br/>
            {address.vatId && <span>NIP: {address.vatId}</span>}
        </div> : <span>Brak</span>}
    </>;

    const onConfirmOrder = useCallback(async (orderStage: OrderStage, fixStock: boolean, parcelSize?: string) => {
        if (!orderDetails?.orderGuid) return;
        try {
            if (selectedPaymentMethod?.value === "ShopPayment") {
                await apiService.getApi(OrderProductApi)
                    .apiOrderProductUpdateStockPatch({
                        orderId: orderDetails.id,
                        shouldUpdateStock: fixStock
                    });
            }
            setOverStock(false);
            await apiService.getApi(OrderApi)
                .apiOrderConfirmOrderPatch(orderDetails?.orderGuid, orderStage, parcelSize);
            message.success("Pomyślnie potwierdzono zamówienie");
            await fetchOrderDetails();
        } catch (e: any) {
            if (e.response?.data === "OverStock") {
                message.error("Przekroczono stan magazynowy produktów");
                await onConfirmOrder(orderStage, true);
                message.info("Zaktualizowano stan zamówienia");
                setOverStock(true);
            } else {
                message.error("Wystąpił błąd podczas potwierdzania zamówienia")
            }
            return e.response?.data;
        }
    }, [apiService, orderDetails?.orderGuid]);

    const canOrderBeConfirmed = () => {
        if (orderDetails?.orderProducts?.length === 0) return false;
        switch (orderDetails?.orderStage) {
            case OrderStage.WaitingForPayment:
                return orderDetails?.deliveryMethod === DeliveryMethod.SelfPickup
            case OrderStage.Paid:
                return orderDetails?.deliveryMethod !== DeliveryMethod.SelfPickup
            default:
                return false;
        }
    }

    const onFinalizeClick = async (fixStock: boolean) => {
        if (!orderDetails?.id) return;
        try {
            await apiService.getApi(OrderProductApi)
                .apiOrderProductUpdateStockPatch({
                    orderId: orderDetails?.id,
                    shouldUpdateStock: fixStock
                });
            setOverStock(false);
        } catch (e: any) {
            if (e.response?.data === "OverStock") {
                message.error("Przekroczono stan magazynowy produktów");
                await onFinalizeClick(true);
                message.info("Zaktualizowano stan zamówienia");
                setOverStock(true);
            }
            return e.response?.data;
        }
    };

    const onLabelDownload = useCallback(async () => {
        if (!orderDetails?.shipmentId) return;

        try {
            const response: any = await apiService.getApi(OrderApi)
                .apiOrderShipmentLabelGet(orderDetails.shipmentId, {responseType: 'blob'});
            const link = document.createElement('a');
            link.href = URL.createObjectURL(response.data!);
            link.download = `${orderDetails?.orderNumber}.pdf`;
            document.body.appendChild(link);
            link.click();
            link.parentNode?.removeChild(link);
        } catch (e: any) {
            if (e.response?.data.includes("shipment_status_incorrect")) {
                message.info("Etykieta do przesyłki nie jest gotowa");
            } else {
                message.error("Wystąpił błąd podczas pobierania etykiety dla zamówienia");
            }
            return e.response?.data;
        }
    }, [orderDetails?.shipmentId]);

    useEffect(() => {
        if (!orderDetails || paymentMethods.length === 0 || !!selectedPaymentMethod) return;
        setSelectedPaymentMethod(paymentMethods.find(x => x.value === orderDetails.paymentMethod?.value)!);
    }, [paymentMethods, orderDetails]);

    useEffect(() => {
        fetchOrderDetails();
    }, [fetchOrderDetails]);

    useEffect(() => {
        fetchPaymentMethods();
    }, [fetchPaymentMethods]);

    useEffect(() => {
        fetchAvailableDeliveryMethod();
    }, [fetchAvailableDeliveryMethod]);

    return <>
        {orderDetails && <>
            <div className={styles.headerContainer}>
                {orderDetails?.orderStage === OrderStage.Confirmed && orderDetails?.deliveryMethod !== DeliveryMethod.SelfPickup &&
                    <AsyncButton type="primary" onClick={() => onConfirmOrder(OrderStage.Sent, false)}>
                        Oznacz jako wysłane
                    </AsyncButton>}
                {orderDetails?.orderStage === OrderStage.Sent && orderDetails?.deliveryMethod !== DeliveryMethod.SelfPickup &&
                    <AsyncButton type="primary" onClick={() => onConfirmOrder(OrderStage.Delivered, false)}>
                        Oznacz jako dostarczone
                    </AsyncButton>}
                {canOrderBeConfirmed() && <>{orderDetails.deliveryMethod === DeliveryMethod.SelfPickup ?
                    <AsyncButtonPopconfirm
                        title={"Czy na pewno chcesz potwierdzić to zamówienie?"}
                        okText={"Potwierdź"} type={"primary"}
                        onConfirm={() => onConfirmOrder(OrderStage.Confirmed, false)}
                        cancelText={"Anuluj"} className={styles.confirmButton}
                        disabled={!canOrderBeConfirmed()}
                    >
                        Potwierdź zamówienie
                    </AsyncButtonPopconfirm> : <Dropdown disabled={!canOrderBeConfirmed()} menu={{
                        items:
                            [{
                                key: "small",
                                label: "Gabaryt A",
                                onClick: () => onConfirmOrder(OrderStage.Confirmed, false, "small")
                            },
                                {
                                    key: "medium",
                                    label: "Gabaryt B",
                                    onClick: () => onConfirmOrder(OrderStage.Confirmed, false, "medium")
                                },
                                {
                                    key: "large",
                                    label: "Gabaryt C",
                                    onClick: () => onConfirmOrder(OrderStage.Confirmed, false, "large")
                                }]
                    }}>
                        <Button disabled={!canOrderBeConfirmed()} type="primary">Potwierdź zamówienie</Button>
                    </Dropdown>}</>}
                <div className={styles.headerText}>
                    Szczegóły zamówienia <strong>{orderDetails.orderNumber}</strong>
                </div>
            </div>
            <div className={styles.orderDetailsContainer}>
                <div style={{display: "flex", flexDirection: "column", justifyContent: "center"}}>
                    <p>
                        Klient:&nbsp;
                        <strong>
                            {orderDetails.client?.firstName ?? orderDetails.unregisteredUserData?.firstName}&nbsp;
                            {orderDetails.client?.lastName ?? orderDetails.unregisteredUserData?.lastName}
                        </strong>
                    </p>
                    <p>
                        Data utworzenia:&nbsp;
                        <strong>{moment(orderDetails.createdAt).format("DD-MM-YYYY HH:mm")}</strong>
                    </p>
                    <p>Adres email: <strong>{orderDetails.email}</strong></p>
                    <p>Numer telefonu: <strong>{orderDetails.phoneNumber}</strong></p>
                    <p>Status zamówienia: {OrderStageTag(orderDetails.orderStage!)}</p>
                    <p>Status płatności: {OrderPaymentTag(orderDetails.orderPaymentStatus!)}</p>
                </div>
                <div className={styles.addressContainer}>
                    {renderOrderAddress("Adres dostawy", orderDetails.deliveryAddress!)}
                    {orderDetails.discountCode && <div style={{marginTop: "50px"}}>
                        <strong>Kod rabatowy</strong>
                        <br/>
                        <br/>
                        <span>Kod:&nbsp;<strong>{orderDetails.discountCode!.code}</strong></span>
                        <br/>
                        <span>Rabat: <strong>{discountTypeTranslation(orderDetails.discountCode.discountType!)?.translation}</strong></span>
                        {orderDetails.discountCode.discountType !== DiscountType.FreeDelivery && <>
                            <br/>
                            <span>
                            Wielkość rabatu: <strong>
                                {orderDetails.discountCode.discountValue} {discountTypeTranslation(orderDetails.discountCode.discountType!)?.sign}
                            </strong>
                        </span>
                        </>}
                    </div>}
                </div>
                <div className={styles.addressContainer}>
                    {orderDetails.billingAddress
                        ? renderOrderAddress("Adres do faktury", orderDetails.billingAddress!)
                        : renderOrderAddress("Adres do faktury", orderDetails.billingAddress!)
                    }
                    <div style={{marginTop: "50px"}}>
                        <div style={{marginBottom: "10px"}}>
                            <strong>Koszty zamówienia</strong>
                        </div>
                        <div>
                            Produkty:&nbsp;
                            <strong>{orderDetails.orderProducts
                                ?.reduce((acc: number, curr: OrderProductDto) => acc + (curr.unitPrice! * curr.amount!), 0).toFixed(2)} zł
                            </strong>
                        </div>
                        <div>
                            Dostawa:&nbsp;
                            <strong>{orderDetails.deliveryCost?.toFixed(2)} zł</strong>
                        </div>
                        <br/>
                        <div>
                            Razem:&nbsp;
                            <strong>{orderDetails.orderValue?.toFixed(2)} zł</strong>
                        </div>
                    </div>
                </div>
                <div style={{
                    display: "flex",
                    justifyContent: "space-between",
                    flexDirection: "column",
                    height: "270px",
                    width: "31%"
                }}>
                    <div style={{display: "flex", justifyContent: "space-around", alignItems: "center"}}>
                        <div>
                            <strong>Metoda płatności</strong>
                            {selectedPaymentMethod ? <div className={styles.paymentMethod}>
                                {typeof selectedPaymentMethod.icon === "string" ? <img
                                    className={styles.paymentMethodIcon}
                                    alt={selectedPaymentMethod.value}
                                    src={selectedPaymentMethod.icon}
                                /> : selectedPaymentMethod.icon}
                                <span style={{textAlign: "center"}}>{selectedPaymentMethod.label}</span>
                                {selectedPaymentMethod.value === "ShopPayment" && <>
                                    <br/>
                                    <AsyncButtonPopconfirm
                                        onConfirm={() => onConfirmOrder(OrderStage.Paid, false)}
                                        disabled={orderDetails.orderStage !== OrderStage.Confirmed || orderDetails.orderProducts?.length == 0}
                                        style={{marginTop: "10px"}} okText={"Potwierdź"} cancelText={"Anuluj"}
                                        title={"Czy chcesz potwierdzić płatność za to zamówienie?"} type={"primary"}
                                    >
                                        Potwierdź płatność
                                    </AsyncButtonPopconfirm>
                                </>}
                            </div> : <div style={{marginTop: '10px'}}>
                                <Tag color={"red"}>Nie wybrano</Tag>
                            </div>}
                        </div>
                        <div className={styles.deliveryMethodContainer}>
                            <strong>Metoda dostawy</strong>
                            {orderDetails.deliveryMethod ? <div className={styles.deliveryMethod}>
                                <img
                                    className={styles.image}
                                    alt={orderDetails.deliveryMethod}
                                    src={DeliveryMethodEnum
                                        .find(x => x.value === orderDetails.deliveryMethod)!.iconSrc
                                    }
                                />
                                <br/>
                                <span>{deliveryMethodTranslation(orderDetails.deliveryMethod)}</span>
                                {orderDetails.inPostParcelMachineDetails && orderDetails.deliveryMethod === DeliveryMethod.InPostParcelMachine &&
                                    <div className={styles.parcelMachineDetailsContainer}>
                                        <span>
                                            Paczkomat:&nbsp;
                                            <strong>{orderDetails.inPostParcelMachineDetails.parcelLockerId}</strong>
                                        </span>
                                        <InfoCircleOutlined
                                            className={styles.infoIcon}
                                            onClick={() => setShowParcelMachineDetailsModal(true)}
                                        />
                                    </div>}
                                {orderDetails.deliveryMethod === DeliveryMethod.SelfPickup && <>
                                    <br/>
                                    <AsyncButtonPopconfirm
                                        type={"primary"} style={{marginTop: "10px"}} okText={"Potwierdź"}
                                        cancelText={"Anuluj"}
                                        title={"Czy chcesz potwierdź odbiór zamówienia?"}
                                        disabled={orderDetails.orderStage === OrderStage.Delivered || orderDetails.orderStage !== OrderStage.Paid}
                                        onConfirm={() => onConfirmOrder(OrderStage.Delivered, false)}
                                    >
                                        Potwierdź odbiór
                                    </AsyncButtonPopconfirm>
                                </>}
                            </div> : <div style={{marginTop: '10px'}}>
                                <Tag color={"red"}>Nie wybrano</Tag>
                            </div>}
                        </div>
                    </div>
                    {orderDetails.optionalMessage && <div className={styles.orderMessageContainer}>
                        <strong className={styles.header}>Wiadomość <br/> do zamówienia:</strong>
                        &nbsp;
                        <div className={styles.text}>
                            {orderDetails.optionalMessage}
                        </div>
                    </div>}
                </div>
            </div>
            <div>
                <h1 style={{marginBottom: "10px"}}>Produkty</h1>
                <OrderProductsListContainer
                    fetchOrderDetails={() => fetchOrderDetails()}
                    order={orderDetails}
                    overStock={overStock}
                    setOverStock={setOverStock}
                    onFinalizeOrder={() => setShowFillOrderModal(true)}
                />
                <h1 style={{marginBottom: "unset"}}>Zwroty</h1>
                <OrderRefundsListContainer
                    orderDetails={orderDetails}
                />
            </div>
        </>}
        <OrderDataFill
            orderDetails={orderDetails}
            updateStock={() => onFinalizeClick(false)}
            showOrderDataModal={showFillOrderModal}
            setShowOrderDataModal={setShowFillOrderModal}
            paymentMethods={paymentMethods}
            selectedPaymentMethod={selectedPaymentMethod}
            setSelectedPaymentMethod={setSelectedPaymentMethod}
            selectedDeliveryMethod={selectedDeliveryMethod}
            setSelectedDeliveryMethod={setSelectedDeliveryMethod}
            availableDeliveryMethods={availableDeliveryMethods}
            selectedParcelMachine={selectedParcelMachine}
            setSelectedParcelMachine={setSelectedParcelMachine}
            refreshOrderDetails={fetchOrderDetails}
        />
        <Modal
            open={showParcelMachineDetailsModal}
            onOk={() => setShowParcelMachineDetailsModal(false)}
            onCancel={() => setShowParcelMachineDetailsModal(false)}
            footer={<div
                style={{justifyContent: orderDetails?.shipmentId ? "space-between" : "end"}}
                className={styles.parcelMachineDetailsFooterContainer}
            >
                {orderDetails?.shipmentId && <AsyncButton
                    disabled={orderDetails.orderStage === OrderStage.Canceled || orderDetails.orderStage === OrderStage.Abandoned}
                    onClick={onLabelDownload}
                >
                    Pobierz etykietę dla zamówienia
                </AsyncButton>}
                <Button onClick={() => setShowParcelMachineDetailsModal(false)} type={"primary"}>
                    Zamknij
                </Button>
            </div>}
            okText={"Zamknij"}
            title={`Szczegóły paczkomatu: ${orderDetails?.inPostParcelMachineDetails?.parcelLockerId}`}
        >
            <span>{orderDetails?.inPostParcelMachineDetails?.addressLine1}</span>
            <br/>
            <span>{orderDetails?.inPostParcelMachineDetails?.addressLine2}</span>
            <br/>
            <br/>
            <span>{orderDetails?.inPostParcelMachineDetails?.locationDescription}</span>
        </Modal>
    </>
}